// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import LineAreaChart from "components/stylishChart";
import StylishPieChart from "components/StylishPieChart";
import { useLazyDashboardDataQuery } from "services/main";
import { useEffect, useState } from "react";
import { useOrderGraphMutation } from "services/main";
import { useRevenueGraphMutation } from "services/main";
import { FormControl, MenuItem, Rating, Select, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useBestSellingMutation } from "services/main";
import MDAvatar from "components/MDAvatar";
import MDTypography from "components/MDTypography";
import { getFromStorage } from "helpers";
import { STORAGE_KEYS } from "helpers";
import useTranslation from "hooks/Translation";

function Dashboard() {
  const navigate = useNavigate();
  const t = useTranslation();
  const [bestSelling] = useBestSellingMutation();
  const { sales, tasks } = reportsLineChartData;
  const [dashboard] = useLazyDashboardDataQuery();
  const [dashboardData, setDashboardData] = useState("");
  const [orderGraph] = useOrderGraphMutation();
  const [revenueGraph] = useRevenueGraphMutation();
  const [orderKeys, setOrderKeys] = useState([]);
  const [revenueKeys, setRevenueKeys] = useState([]);
  const [orderValues, setOrderValues] = useState([]);
  const [bestSellingData, setBestSellingData] = useState([]);
  const [revenueValues, setRevenueValues] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState("3");
  const [selectedRevenue, setSelectedRevenue] = useState("3");
  const currency = getFromStorage(STORAGE_KEYS.currency);

  function convertToInternationalCurrencySystem(labelValue) {
    const num = Math.abs(Number(labelValue));

    if (num >= 1.0e9) return (num / 1.0e9).toFixed(2) + " B"; // Billions
    if (num >= 1.0e6) return (num / 1.0e6).toFixed(2) + " M"; // Millions
    if (num >= 1.0e3) return (num / 1.0e3).toFixed(2) + " K"; // Thousands
    return num.toFixed(2); // Less than 1K
  }

  const getDashboard = async () => {
    try {
      const res = await dashboard({}).unwrap();
      if (res?.status === 200) {
        setDashboardData(res?.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getBestSelling = async () => {
    try {
      const res = await bestSelling({}).unwrap();
      if (res?.status === 200) {
        setBestSellingData(res?.data);
      } else {
        setBestSellingData([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getOrderGraph = async () => {
    try {
      let body = { interval: Number(selectedOrder) };
      const res = await orderGraph(body).unwrap();

      if (res?.status === 200 && res?.data) {
        setOrderKeys(Object.keys(res.data)); // Extracts months (keys)
        setOrderValues(Object.values(res.data)); // Extracts order values
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getRevenueGraph = async () => {
    try {
      let body = { interval: Number(selectedRevenue) };
      const res = await revenueGraph(body).unwrap();

      if (res?.status === 200 && res?.data) {
        setRevenueKeys(Object.keys(res.data));
        setRevenueValues(Object.values(res.data)); // Extracts revenue values
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getDashboard();
    getBestSelling();
  }, []);

  useEffect(() => {
    getOrderGraph();
    getRevenueGraph();
  }, [selectedOrder, selectedRevenue]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="weekend"
                title={t.main.monthlySales}
                onClick={() => navigate("/payment-management")}
                count={
                  dashboardData?.monthly_revenue !== undefined
                    ? `${
                        currency === "AED" ? "AED" : "USD"
                      } ${convertToInternationalCurrencySystem(
                        currency === "AED"
                          ? dashboardData?.monthly_revenue || 0
                          : dashboardData?.monthly_revenue_usd || 0
                      )}`
                    : "-"
                }
                // percentage={{
                //   color: "success",
                //   amount: "+55%",
                //   label: "than lask week",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="leaderboard"
                title={t.main.commissionPaid}
                count={
                  currency === "AED"
                    ? dashboardData?.commission_paid || 0
                    : dashboardData?.commission_paid_usd || 0
                }
                // percentage={{
                //   color: "success",
                //   amount: "+3%",
                //   label: "than last month",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="store"
                title={t.main.revenue}
                onClick={() => navigate("/payment-management")}
                count={
                  dashboardData?.total_revenue !== undefined
                    ? `${
                        currency === "AED" ? "AED" : "USD"
                      } ${convertToInternationalCurrencySystem(
                        currency === "AED"
                          ? dashboardData?.total_revenue || 0
                          : dashboardData?.total_revenue_usd || 0
                      )}`
                    : "-"
                }
                // percentage={{
                //   color: "success",
                //   amount: "+1%",
                //   label: "than yesterday",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="person_add"
                title={t.main.newOrders}
                onClick={() => navigate("/orders-management")}
                count={dashboardData?.new_orders}
                // percentage={{
                //   color: "success",
                //   amount: "",
                //   label: "Just updated",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="person_add"
                title={t.main.pendingQuotes}
                onClick={() => navigate("/managing-quotes")}
                count={dashboardData?.pending_quote_count || "0"}
                // percentage={{
                //   color: "success",
                //   amount: "",
                //   label: "Just updated",
                // }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="theme-1"
                icon="person_add"
                title={t.main.completedOrders}
                onClick={() =>
                  navigate("/orders-management", { state: { value: 1 } })
                }
                count={dashboardData?.completed_orders || "0"}
                // percentage={{
                //   color: "success",
                //   amount: "",
                //   label: "Just updated",
                // }}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5} mb={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox sx={{ position: "relative" }}>
                <MDBox
                  sx={{
                    position: "absolute",
                    zIndex: 1,
                    right: 0,
                    margin: 1,
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      className="select_div"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectedOrder}
                      displayEmpty
                      onChange={(val) => setSelectedOrder(val?.target?.value)}
                      sx={{
                        width: 150,
                        border: "1px solid grey", // Add a custom border color
                        borderRadius: "6px", // Optional: Add border radius for styling
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Adjust border color for the outlined variant
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color on hover
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color when focused
                        },
                      }}
                    >
                      <MenuItem value={"1"}>{t.main.daily}</MenuItem>
                      <MenuItem value={"2"}>{t.main.weekly}</MenuItem>
                      <MenuItem value={"3"}>{t.main.monthly}</MenuItem>
                      <MenuItem value={"4"}>{t.main.yearly}</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <LineAreaChart
                  keys={orderKeys}
                  values={orderValues}
                  label={t.main.orders}
                />
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3} sx={{ position: "relative" }}>
                <MDBox
                  sx={{
                    position: "absolute",
                    zIndex: 1,
                    right: 0,
                    margin: 1,
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      className="select_div"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectedRevenue}
                      displayEmpty
                      onChange={(val) => setSelectedRevenue(val?.target?.value)}
                      sx={{
                        width: 150,
                        border: "1px solid grey", // Add a custom border color
                        borderRadius: "6px", // Optional: Add border radius for styling
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Adjust border color for the outlined variant
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color on hover
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "grey", // Border color when focused
                        },
                      }}
                    >
                      <MenuItem value={"1"}>{t.main.daily}</MenuItem>
                      <MenuItem value={"2"}>{t.main.weekly}</MenuItem>
                      <MenuItem value={"3"}>{t.main.monthly}</MenuItem>
                      <MenuItem value={"4"}>{t.main.yearly}</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <LineAreaChart
                  keys={revenueKeys}
                  values={revenueValues}
                  label={t.main.sales}
                  backgroundColor="rgba(144, 238, 144, 0.2)"
                  borderColor="rgba(144, 238, 144, 1)"
                  pointBackgroundColor="rgba(144, 238, 144, 1)"
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Projects data={bestSellingData} />
            </Grid>
            {/* <Grid item xs={12} md={6} lg={4}>
              <OrdersOverview />
            </Grid> */}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
